import { useEffect, useState } from "react";
import { OuSelect, useOuTree } from "@/components/OuPicker";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { apiErr } from "@/lib/api";
import { fieldLimit, firstLengthError } from "@/lib/fieldLimits";
import { Building2, Plus, Pencil, Trash2, X, UserCheck, Sparkles } from "lucide-react";
import { EmployeePicker } from "@/components/EmployeePicker";

const EMPTY = { name: "", jobTitles: [], departments: [], streetAddress: "", postalCode: "", city: "", isDefault: false, defaultDomain: "", defaultOuDn: "" };

export default function Companies() {
  const { t } = useLang();
  const { can } = useAuth();
  const isManager = can("companies.manage");
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [domains, setDomains] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [jt, setJt] = useState("");
  const [dept, setDept] = useState("");
  const { tree: ouTree, count: ouCount } = useOuTree();

  const load = () => api.get("/companies").then((r) => setCompanies(r.data));
  useEffect(() => { load(); api.get("/users").then((r) => setUsers(r.data)); api.get("/domains").then((r) => setDomains(r.data)).catch(() => {}); }, []);

  const startNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const startEdit = (c) => { setEditing(c); setForm({ ...EMPTY, ...c }); setOpen(true); };

  const addJobTitle = () => { if (jt.trim() && !form.jobTitles.includes(jt.trim())) setForm({ ...form, jobTitles: [...form.jobTitles, jt.trim()] }); setJt(""); };
  const addDept = () => { if (dept.trim() && !form.departments.find((d) => d.name === dept.trim())) setForm({ ...form, departments: [...form.departments, { name: dept.trim(), managerId: null, managerName: "", streetAddress: "", postalCode: "", city: "" }] }); setDept(""); };
  const setDeptMgr = (i, mid) => {
    const u = users.find((x) => x.id === mid);
    const next = [...form.departments];
    next[i] = { ...next[i], managerId: mid, managerName: u?.displayName || "" };
    setForm({ ...form, departments: next });
  };
  const setDeptName = (i, val) => {
    const next = [...form.departments];
    next[i] = { ...next[i], name: val };
    setForm({ ...form, departments: next });
  };
  const setJobTitle = (i, val) => {
    const next = [...form.jobTitles];
    next[i] = val;
    setForm({ ...form, jobTitles: next });
  };
  const setDeptAddr = (i, key, val) => {
    const next = [...form.departments];
    next[i] = { ...next[i], [key]: val };
    setForm({ ...form, departments: next });
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error(t("companies.nameRequired")); return; }
    const lenErr = firstLengthError([
      [t("companies.name"), form.name, "companyName"],
      [t("field.street"), form.streetAddress, "streetAddress"],
      [t("field.zip"), form.postalCode, "postalCode"],
      [t("field.city"), form.city, "city"],
      ...form.jobTitles.map((j) => [t("companies.jobTitles"), j, "jobTitle"]),
      ...form.departments.flatMap((d) => [
        [t("companies.departments"), d.name, "department"],
        [t("field.street"), d.streetAddress, "streetAddress"],
        [t("field.zip"), d.postalCode, "postalCode"],
        [t("field.city"), d.city, "city"],
      ]),
    ]);
    if (lenErr) { toast.error(lenErr); return; }
    try {
      const body = { name: form.name, jobTitles: form.jobTitles, departments: form.departments,
        streetAddress: form.streetAddress, postalCode: form.postalCode, city: form.city,
        isDefault: !!form.isDefault, defaultDomain: form.defaultDomain || "", defaultOuDn: form.defaultOuDn || "" };
      if (editing) await api.put(`/companies/${editing.id}`, body);
      else await api.post("/companies", body);
      toast.success(t("companies.saved")); setOpen(false); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const remove = async (id) => { if (!window.confirm(t("companies.confirmDelete"))) return; await api.delete(`/companies/${id}`); load(); };

  const [building, setBuilding] = useState(false);
  const buildFromTenant = async () => {
    if (!window.confirm(t("companies.buildConfirm"))) return;
    setBuilding(true);
    try {
      const { data } = await api.post("/companies/build-from-tenant");
      toast.success(t("companies.buildDone").replace("{n}", data.length));
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBuilding(false); }
  };

  return (
    <>
      <PageHeader title={t("nav.companies")} subtitle={t("companies.subtitle")}>
        {isManager && companies.length === 0 && (
          <Button onClick={buildFromTenant} disabled={building} variant="outline" data-testid="company-build-btn" className="mr-2 rounded-sm border-zinc-300">
            <Sparkles size={16} className="mr-2" /> {building ? t("companies.building") : t("companies.buildFromTenant")}
          </Button>
        )}
        {isManager && (
          <Button onClick={startNew} data-testid="company-new-btn" className="rounded-sm"><Plus size={16} className="mr-2" /> {t("companies.new")}</Button>
        )}
      </PageHeader>
      <div className="grid grid-cols-1 gap-px bg-zinc-200 p-6 md:grid-cols-2 md:p-10">
        {companies.length === 0 && <div className="bg-white p-10 text-center text-sm text-zinc-400 md:col-span-2">{t("companies.empty")}</div>}
        {companies.map((c) => (
          <div key={c.id} className="bg-white p-5" data-testid={`company-card-${c.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2"><Building2 size={18} className="text-primary" /><span className="text-sm font-bold text-zinc-900">{c.name}</span>
                {c.isDefault && <span data-testid={`company-default-badge-${c.id}`} className="border border-primary/20 bg-primary/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-primary">{t("companies.default")}</span>}
              </div>
              {isManager && (
                <div className="flex gap-1">
                  <button onClick={() => startEdit(c)} data-testid={`company-edit-${c.id}`} className="p-1.5 text-zinc-400 hover:text-zinc-900"><Pencil size={15} /></button>
                  <button onClick={() => remove(c.id)} data-testid={`company-delete-${c.id}`} className="p-1.5 text-zinc-400 hover:text-red-600"><Trash2 size={15} /></button>
                </div>
              )}
            </div>
            <div className="mt-3 space-y-2">
              {c.defaultDomain && <div className="text-xs text-zinc-500" data-testid={`company-domain-${c.id}`}><span className="font-mono text-zinc-700">@{c.defaultDomain}</span></div>}
              <div><div className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">{t("companies.departments")}</div>
                {c.departments.length === 0 ? <span className="text-xs text-zinc-400">—</span> :
                  c.departments.map((d) => <div key={d.name} className="text-xs text-zinc-600">{d.name} {d.managerName && <span className="text-zinc-400">· {t("companies.mgr")} {d.managerName}</span>}</div>)}
              </div>
              <div><div className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">{t("companies.jobTitles")}</div>
                <div className="flex flex-wrap gap-1">{c.jobTitles.map((j) => <span key={j} className="border border-zinc-200 px-1.5 py-0.5 text-[10px] text-zinc-600">{j}</span>)}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm sm:max-w-2xl">
          <DialogHeader><DialogTitle>{editing ? t("companies.editTitle") : t("companies.newTitle")}</DialogTitle></DialogHeader>
          <div className="max-h-[65vh] space-y-5 overflow-y-auto pr-1">
            <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("companies.name")}</Label>
              <Input value={form.name} maxLength={fieldLimit("companyName")} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="company-name" className="rounded-sm border-zinc-300" /></div>

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("companies.defaultDomain")}</Label>
                {domains.length ? (
                  <Select value={form.defaultDomain || "__none__"} onValueChange={(v) => setForm({ ...form, defaultDomain: v === "__none__" ? "" : v })}>
                    <SelectTrigger className="rounded-sm border-zinc-300 font-mono text-sm" data-testid="company-domain"><SelectValue placeholder={t("companies.selectDomain")} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">{t("companies.noDomain")}</SelectItem>
                      {domains.map((d) => <SelectItem key={d.id} value={d.id}>{d.id}{d.isDefault ? ` ${t("onb.tenantDefault")}` : ""}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input value={form.defaultDomain || ""} onChange={(e) => setForm({ ...form, defaultDomain: e.target.value })} data-testid="company-domain" className="rounded-sm border-zinc-300 font-mono text-sm" />
                )}
                <p className="text-[11px] text-zinc-400">{t("companies.defaultDomainHint")}</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("companies.defaultOu")}</Label>
                <OuSelect value={form.defaultOuDn || ""} onChange={(v) => setForm({ ...form, defaultOuDn: v })} tree={ouTree} count={ouCount} testid="company-ou" placeholder={t("ou.pick")} />
                <p className="text-[11px] text-zinc-400">{t("companies.defaultOuHint")}</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("companies.setDefault")}</Label>
                <div className="flex items-center gap-2 pt-1.5">
                  <Switch checked={!!form.isDefault} onCheckedChange={(v) => setForm({ ...form, isDefault: v })} data-testid="company-default-toggle" />
                  <span className="text-xs text-zinc-500">{t("companies.setDefaultHint")}</span>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("companies.address")}</Label>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                <Input value={form.streetAddress || ""} maxLength={fieldLimit("streetAddress")} onChange={(e) => setForm({ ...form, streetAddress: e.target.value })} placeholder={t("field.street")} data-testid="company-street" className="rounded-sm border-zinc-300" />
                <Input value={form.postalCode || ""} maxLength={fieldLimit("postalCode")} onChange={(e) => setForm({ ...form, postalCode: e.target.value })} placeholder={t("field.zip")} data-testid="company-zip" className="rounded-sm border-zinc-300" />
                <Input value={form.city || ""} maxLength={fieldLimit("city")} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder={t("field.city")} data-testid="company-city" className="rounded-sm border-zinc-300" />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("companies.deptsWithMgr")}</Label>
              {form.departments.map((d, i) => (
                <div key={i} className="space-y-2 border border-zinc-200 p-3">
                  <div className="flex items-center gap-2">
                    <Input value={d.name} maxLength={fieldLimit("department")} onChange={(e) => setDeptName(i, e.target.value)} placeholder={t("companies.addDept")} data-testid={`dept-name-${i}`} className="w-40 shrink-0 rounded-sm border-zinc-300 text-sm font-semibold" />
                    <EmployeePicker value={d.managerId || ""} onChange={(v) => setDeptMgr(i, v)} users={users}
                      placeholder={t("companies.assignMgr")} searchPlaceholder={t("onb.cloneSearch")} emptyText={t("onb.cloneNoMatch")}
                      noneValue="" noneLabel={t("onb.noManager")} testid={`dept-mgr-${i}`} className="flex-1" />
                    <button onClick={() => setForm({ ...form, departments: form.departments.filter((_, j) => j !== i) })} className="p-1 text-zinc-400 hover:text-red-600"><X size={15} /></button>
                  </div>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                    <Input value={d.streetAddress || ""} maxLength={fieldLimit("streetAddress")} onChange={(e) => setDeptAddr(i, "streetAddress", e.target.value)} placeholder={t("field.street")} data-testid={`dept-street-${i}`} className="rounded-sm border-zinc-300 text-sm" />
                    <Input value={d.postalCode || ""} maxLength={fieldLimit("postalCode")} onChange={(e) => setDeptAddr(i, "postalCode", e.target.value)} placeholder={t("field.zip")} data-testid={`dept-zip-${i}`} className="rounded-sm border-zinc-300 text-sm" />
                    <Input value={d.city || ""} maxLength={fieldLimit("city")} onChange={(e) => setDeptAddr(i, "city", e.target.value)} placeholder={t("field.city")} data-testid={`dept-city-${i}`} className="rounded-sm border-zinc-300 text-sm" />
                  </div>
                  <div>
                    <Label className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">{t("companies.deptOu")}</Label>
                    <OuSelect value={d.defaultOuDn || ""} onChange={(v) => setDeptAddr(i, "defaultOuDn", v)} tree={ouTree} count={ouCount} testid={`dept-ou-${i}`} placeholder={t("ou.pick")} />
                  </div>
                </div>
              ))}
              <div className="flex gap-2">
                <Input value={dept} maxLength={fieldLimit("department")} onChange={(e) => setDept(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addDept())} placeholder={t("companies.addDept")} data-testid="company-dept-input" className="rounded-sm border-zinc-300" />
                <Button type="button" variant="outline" onClick={addDept} className="rounded-sm">{t("common.add")}</Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("companies.jobTitles")}</Label>
              <div className="space-y-1.5">
                {form.jobTitles.map((j, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input value={j} maxLength={fieldLimit("jobTitle")} onChange={(e) => setJobTitle(i, e.target.value)} data-testid={`company-jt-${i}`} className="rounded-sm border-zinc-300 text-sm" />
                    <button onClick={() => setForm({ ...form, jobTitles: form.jobTitles.filter((_, x) => x !== i) })} data-testid={`company-jt-remove-${i}`} className="p-1.5 text-zinc-400 hover:text-red-600"><X size={15} /></button>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Input value={jt} maxLength={fieldLimit("jobTitle")} onChange={(e) => setJt(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addJobTitle())} placeholder={t("companies.addJt")} data-testid="company-jt-input" className="rounded-sm border-zinc-300" />
                <Button type="button" variant="outline" onClick={addJobTitle} className="rounded-sm">{t("common.add")}</Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-sm">{t("common.cancel")}</Button>
            <Button onClick={save} data-testid="company-save" className="rounded-sm"><UserCheck size={15} className="mr-2" /> {t("common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
